import {
  getLowerCaseLanguageCode,
  getTranslation,
} from '@mahawi/eshop-common/src/translation';
import { type IProductFeatureFlag } from '@mahawi/eshop-common/src/types';
import { Alert, Button, Collapse, Divider, Popconfirm, Skeleton } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router';

import DetailPageHeader from '../components/detail-page-header';
import LanguageButtons from '../components/language-buttons';
import BrandAndIdentification from '../components/product/brand-and-identification';
import Categories from '../components/product/categories';
import Dimensions from '../components/product/dimensions';
import EbayProduct from '../components/product/ebay-product';
import MicroDashboard from '../components/product/micro-dashboard';
import Photo from '../components/product/photo';
import Prices from '../components/product/prices';
import ProductLanguage from '../components/product/product-language';
import ProductSafety from '../components/product/product-safety';
import Properties from '../components/product/properties';
import Related from '../components/product/related';
import Stock from '../components/product/stock';
import SectionLabel from '../components/section-label';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { featureFlagTags } from '../fragments/feature-flag-tags';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { useSocketSubscription } from '../hooks/use-socket-subscription';
import { type RootState } from '../reducers';
import {
  load as productLoad,
  productFeatureFlagDestroy,
  productSetActive,
} from '../reducers/product/reducer';
import Socket from '../socket';

function ProductContainer({
  socket = undefined,
}: {
  socket?: Socket;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const productUUIDState = useSelector((s: RootState) => s.Product.uuid);
  const productNames = useSelector((s: RootState) => s.Product.names);
  const productIsActive = useSelector((s: RootState) => s.Product.isActive);
  const productFeatureFlags = useSelector(
    (s: RootState) => s.Product.featureFlags,
  );
  const productBrand = useSelector((s: RootState) => s.Product.brand);
  const productMpn = useSelector((s: RootState) => s.Product.mpn);
  const productCategories = useSelector((s: RootState) => s.Product.categories);
  const productPhotos = useSelector((s: RootState) => s.Product.photos);
  const productDimensions = useSelector((s: RootState) => s.Product.dimensions);
  const productWarehouses = useSelector(
    (s: RootState) => s.Product.productWarehouses,
  );
  const productPrices = useSelector((s: RootState) => s.Product.prices);
  const productRelatedUUIDs = useSelector(
    (s: RootState) => s.Product.relatedUUIDs,
  );
  const productInProcess = useSelector((s: RootState) => s.Product.inProcess);
  const productUpdatedAt = useSelector((s: RootState) => s.Product.updatedAt);
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const configUrl = useSelector((s: RootState) => s.Config.url);
  const featureFlagsState = useSelector((s: RootState) => s.FeatureFlags);
  const warehouseState = useSelector((s: RootState) => s.Warehouse);

  const { productUUID } = useParams();
  const [searchParams] = useSearchParams();
  const openPanels = useMemo(
    () =>
      [...searchParams.entries()]
        .filter(([, v]) => v === 'open')
        .map(([k]) => k),
    [searchParams],
  );
  const photoOpen = openPanels.includes('photo');
  const photoAnchorRef = useRef<HTMLSpanElement>(null);

  const [languageTypeCodeError, setLanguageTypeCodeError] = useState<string[]>(
    [],
  );

  const handleSetLanguageTypeCodeError = useCallback(
    (codes: string[]): void => {
      setLanguageTypeCodeError(codes);
    },
    [],
  );

  const tagOnClose = useCallback(
    (uuid: string, productFeatureFlag: IProductFeatureFlag): void => {
      dispatch(productFeatureFlagDestroy(uuid, productFeatureFlag.code));
    },
    [dispatch],
  );

  useEffect((): void => {
    if (productUUID) {
      dispatch(productLoad(productUUID));
    }
  }, [dispatch, productUUID]);

  useEffect((): void => {
    if (photoOpen && photoAnchorRef.current) {
      photoAnchorRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [photoOpen, productUpdatedAt]);

  const productNameLocalized = useMemo(
    () => getTranslation(productNames, languageType) || EMPTY_VALUE,
    [productNames, languageType, productUpdatedAt],
  );

  const visibilityElement = useMemo((): React.ReactElement => {
    const buttonElement: React.ReactElement = productIsActive ? (
      <Button type="primary">Product is active</Button>
    ) : (
      <Button danger>Product is disabled</Button>
    );

    return (
      <Popconfirm
        placement="bottom"
        title={productIsActive ? 'Disable product' : 'Enable product'}
        description={
          productIsActive
            ? 'Product will be disabled for export to CSS and PCP.'
            : 'Product will be enabled for export to CSS and PCP.'
        }
        onConfirm={(): void => {
          dispatch(productSetActive(productUUIDState, !productIsActive));
        }}
        okText="Yes"
        cancelText="No"
      >
        {buttonElement}
      </Popconfirm>
    );
  }, [productIsActive, productUUIDState, dispatch]);

  const featureFlagEls = useMemo(
    () =>
      featureFlagTags({
        productUUID: productUUIDState,
        productFeatureFlags,
        FeatureFlags: featureFlagsState,
        onClose: tagOnClose,
        Warehouse: warehouseState,
      }),
    [
      featureFlagsState,
      productFeatureFlags,
      productUUIDState,
      tagOnClose,
      warehouseState,
    ],
  );

  useSocketSubscription(
    socket,
    (s) => s.joinProductRoom({ productUUID: productUUIDState }),
    [productUUIDState],
  );

  const collapseItems = useMemo(() => {
    const stockInternal = productWarehouses.reduce((sum, wh) => {
      const detail = warehouseState.warehouses?.find((w) => w.uuid === wh.uuid);
      return detail?.isInternal ? sum + wh.count : sum;
    }, 0);
    const stockExternal = productWarehouses.reduce((sum, wh) => {
      const detail = warehouseState.warehouses?.find((w) => w.uuid === wh.uuid);
      return !detail?.isInternal && wh.warehouse.isPrimary
        ? sum + wh.count
        : sum;
    }, 0);

    const eurPrice =
      productPrices.find((p) => p.isoCode === 'EUR')?.priceWithoutTax ?? 0;
    const primaryWarehouse = productWarehouses.find(
      (w) => w.warehouse.isPrimary,
    );
    const purchasePriceEUR = primaryWarehouse?.pricesEUR?.purchase ?? 0;
    const profitEUR =
      eurPrice > 0 && purchasePriceEUR > 0 ? eurPrice - purchasePriceEUR : 0;
    const profitPercent =
      purchasePriceEUR > 0 ? (profitEUR / purchasePriceEUR) * 100 : 0;
    const profitLabel =
      purchasePriceEUR > 0
        ? `${profitEUR.toFixed(2)} EUR (${profitPercent.toFixed(2)} %)`
        : EMPTY_VALUE;

    return [
      {
        key: 'language',
        label: (
          <SectionLabel
            title="Language"
            ok={languageTypeCodeError.length === 0}
            errors={languageTypeCodeError}
          />
        ),
        children: (
          <ProductLanguage
            setLanguageTypeCodeError={handleSetLanguageTypeCodeError}
          />
        ),
      },
      {
        key: 'brand',
        label: (
          <SectionLabel
            title="Brand & Identification"
            ok={!!productBrand?.uuid && !!productMpn}
          />
        ),
        children: <BrandAndIdentification />,
      },
      {
        key: 'categories',
        label: (
          <SectionLabel
            title="Categories"
            ok={!!productCategories.uuids.length}
          />
        ),
        children: <Categories />,
      },
      {
        key: 'photo',
        label: (
          <SectionLabel
            title={`Photo (${productPhotos.length})`}
            ok={!!productPhotos.length}
          />
        ),
        children: (
          <>
            <span
              ref={photoAnchorRef}
              style={{ display: 'block', height: 0 }}
            />
            <Photo />
          </>
        ),
      },
      {
        key: 'dimensions',
        label: (
          <SectionLabel title="Dimensions" ok={!!productDimensions.weight} />
        ),
        children: <Dimensions />,
      },
      {
        key: 'stock',
        label: (
          <SectionLabel
            title={`Stock (${stockInternal} + ${stockExternal} = ${stockInternal + stockExternal})`}
            ok={productWarehouses.some((wh) => wh.count > 0)}
          />
        ),
        children: <Stock />,
      },
      {
        key: 'prices',
        label: (
          <SectionLabel
            title={`Prices ${eurPrice > 0 ? `${eurPrice} EUR` : EMPTY_VALUE} · ${profitLabel}`}
            ok={
              productPrices.length > 0 &&
              productPrices.every(
                (p) =>
                  p.priceWithoutTax > 0 &&
                  p.pricesWithTax.every((cp) => cp.price > 0),
              )
            }
          />
        ),
        children: <Prices />,
      },
      {
        key: 'properties',
        label: 'Properties',
        children: <Properties />,
      },
      {
        key: 'related',
        label: (
          <SectionLabel
            title={`Related products (${productRelatedUUIDs.length})`}
            ok={productRelatedUUIDs.length > 0}
          />
        ),
        children: <Related />,
      },
      { key: 'ebay', label: 'eBay', children: <EbayProduct /> },
      {
        key: 'productSafety',
        label: 'Product safety (GPSR)',
        children: <ProductSafety />,
      },
    ];
  }, [
    languageTypeCodeError,
    handleSetLanguageTypeCodeError,
    productBrand,
    productMpn,
    productCategories,
    productPhotos,
    productDimensions,
    productWarehouses,
    productPrices,
    productRelatedUUIDs,
    warehouseState,
  ]);

  if (!productInProcess && productUpdatedAt && productNames.length === 0) {
    return (
      <Alert
        title="Product not found"
        description={`Product with UUID ${productUUID} was not found.`}
        type="error"
        showIcon
      />
    );
  }

  if (productNames.length === 0 || !productUUID) {
    return <Skeleton active />;
  }

  return (
    <>
      <DetailPageHeader
        title={productNameLocalized}
        onReload={(): void => {
          dispatch(productLoad(productUUID));
        }}
        isReloadDisabled={productInProcess}
        actions={visibilityElement}
        tags={featureFlagEls}
        uuid={productUUID}
        externalUrl={`${configUrl}/${getLowerCaseLanguageCode(languageType)}/product/${productUUIDState}`}
      />

      <MicroDashboard productUUID={productUUID} />

      <Divider />

      <LanguageButtons languageTypeCodeError={languageTypeCodeError} />

      <Divider />

      <Collapse
        defaultActiveKey={openPanels.length > 0 ? openPanels : []}
        items={collapseItems}
      />
    </>
  );
}

export default ProductContainer;
